import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, UserRound, Radar, Send, LogOut, Zap, Crown, Waypoints,
  BookOpen, Menu, ChevronDown, Home, Bell, ArrowLeft, Building2, BookmarkCheck, Settings as SettingsIcon, MailPlus, MailSearch, HelpCircle, Tag, Receipt, UsersRound, MessagesSquare, LifeBuoy,
} from "lucide-react";
import { getAppConfig } from "@/lib/config";
import api from "@/lib/api";

const PRIMARY = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-overview" },
  { to: "/app/discover", label: "Discover & Apply", icon: Radar, testid: "nav-discover" },
  { to: "/app/applications", label: "Applications", icon: Send, testid: "nav-applications" },
  { to: "/app/communications", label: "Communications", icon: MessagesSquare, testid: "nav-communications" },
];
const COMPANIES_CHILDREN = [
  { to: "/app/companies", label: "All Companies", icon: Building2, end: true, testid: "nav-companies-all" },
  { to: "/app/companies/saved", label: "Saved Companies", icon: BookmarkCheck, testid: "nav-companies-saved" },
];
const HELP_MAP = [
  ["/app/discover", "discover"],
  ["/app/applications", "applications"],
  ["/app/companies", "companies"],
  ["/app/profile", "profile"],
  ["/app/loops", "loops"],
  ["/app/admin/templates", "templates"],
  ["/app/admin/contact-audit", "audit"],
  ["/app/admin/settings", "settings"],
  ["/app", "dashboard"],
];

const MOBILE_BOTTOM = [  { to: "/app", label: "Home", icon: Home, end: true, testid: "mnav-overview" },
  { to: "/app/discover", label: "Discover", icon: Radar, testid: "mnav-discover" },
  { to: "/app/applications", label: "Applications", icon: Send, testid: "mnav-applications" },
  { to: "/app/communications", label: "Inbox", icon: MessagesSquare, testid: "mnav-communications" },
  { to: "/app/profile", label: "Profile", icon: UserRound, testid: "mnav-profile" },
];

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

function TrialPill({ onClick }) {
  const { quota } = useAuth();
  if (!quota) return null;
  if (quota.is_pro) {
    return (
      <span data-testid="trial-counter-badge" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold">
        <Crown className="w-3.5 h-3.5" /> Pro
      </span>
    );
  }
  const rem = quota.remaining ?? 0;
  const empty = rem <= 0;
  return (
    <button data-testid="trial-counter-badge" onClick={empty ? onClick : undefined}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${
        empty ? "bg-rose-50 border-rose-200 text-rose-600 hover:bg-rose-100 cursor-pointer" : "bg-amber-50 border-amber-200 text-amber-700"
      }`}>
      <Zap className="w-3.5 h-3.5" /> {empty ? "Upgrade" : `${rem} free left`}
    </button>
  );
}

function Logo() {
  return (
    <Link to="/app" className="flex items-center">
      <img src="/logo-jbn.png" alt="JobBasket Network" className="h-8 w-auto object-contain" />
    </Link>
  );
}

export default function DashboardLayout() {
  const { user, logout, quota, openUpgrade } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawer, setDrawer] = useState(false);
  const [mainAppUrl, setMainAppUrl] = useState("https://jobbasket.in/login");
  const [isAdmin, setIsAdmin] = useState(false);
  const companiesActive = location.pathname.startsWith("/app/companies");
  const [companiesOpen, setCompaniesOpen] = useState(companiesActive);
  const [unreadReplies, setUnreadReplies] = useState(0);
  const initial = (user?.name || user?.email || "?")[0].toUpperCase();

  useEffect(() => {
    const fetchUnread = () => api.get("/applications/replies/summary")
      .then((r) => setUnreadReplies(r.data.unread || 0)).catch(() => {});
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    window.addEventListener("replies-updated", fetchUnread);
    return () => { clearInterval(id); window.removeEventListener("replies-updated", fetchUnread); };
  }, [location.pathname]);

  useEffect(() => {
    getAppConfig().then((c) => { if (c.main_app_url) setMainAppUrl(c.main_app_url); });
    api.get("/admin/access").then((r) => setIsAdmin(!!r.data.is_admin)).catch(() => {});
  }, []);

  useEffect(() => { if (companiesActive) setCompaniesOpen(true); }, [companiesActive]);

  const handleLogout = () => { logout(); navigate("/"); };
  const goMainApp = () => { window.location.href = mainAppUrl; };

  const sideNavClass = ({ isActive }) =>
    `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
      isActive ? "bg-amber-50 text-amber-600" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
    }`;

  const childNavClass = ({ isActive }) =>
    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${
      isActive ? "bg-amber-50 text-amber-600" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
    }`;

  return (
    <div className="min-h-screen bg-[#F5F6F8] lg:flex">
      {/* ===== Desktop left sidebar ===== */}
      <aside className="hidden lg:flex lg:flex-col w-64 shrink-0 sticky top-0 h-screen bg-white border-r border-slate-200">
        <div className="h-16 px-5 flex items-center border-b border-slate-100">
          <Logo />
        </div>
        <nav className="flex-1 px-3 py-4 overflow-y-auto flex flex-col">
          {/* MAIN */}
          <div className="space-y-1">
            <p className="px-3.5 pt-1 pb-2 text-[11px] font-semibold tracking-widest text-slate-400 uppercase">Main</p>
            {PRIMARY.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.testid} className={sideNavClass}>
                <item.icon className="w-5 h-5 shrink-0" /> <span className="flex-1">{item.label}</span>
                {item.to === "/app/communications" && unreadReplies > 0 && (
                  <span data-testid="nav-replies-badge"
                    className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-black text-[11px] font-bold">
                    {unreadReplies > 9 ? "9+" : unreadReplies}
                  </span>
                )}
              </NavLink>
            ))}

            {/* Manage Companies group */}
            <div>
              <button
                data-testid="nav-companies"
                onClick={() => setCompaniesOpen((o) => !o)}
                className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  companiesActive ? "text-amber-600 bg-amber-50/60" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                }`}
              >
                <Building2 className="w-5 h-5 shrink-0" />
                <span className="flex-1 text-left">Manage Companies</span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${companiesOpen ? "rotate-180" : ""}`} />
              </button>
              {companiesOpen && (
                <div className="mt-1 ml-5 pl-4 border-l border-slate-200 space-y-0.5" data-testid="nav-companies-submenu">
                  {COMPANIES_CHILDREN.map((item) => (
                    <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.testid} className={childNavClass}>
                      <item.icon className="w-4 h-4 shrink-0" /> {item.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ACCOUNT */}
          <div className="mt-4 pt-4 border-t border-slate-100 space-y-1">
            <p className="px-3.5 pb-2 text-[11px] font-semibold tracking-widest text-slate-400 uppercase">Account</p>
            <NavLink to="/app/profile" data-testid="nav-profile" className={sideNavClass}>
              <UserRound className="w-5 h-5 shrink-0" /> Profile &amp; Resume
            </NavLink>
            <NavLink to={isAdmin ? "/app/admin/plans" : "/app/pricing"} data-testid="nav-plans" className={sideNavClass}>
              <Tag className="w-5 h-5 shrink-0" /> Plans &amp; Pricing
            </NavLink>
            <NavLink to={isAdmin ? "/app/admin/billing" : "/app/billing"} data-testid="nav-billing" className={sideNavClass}>
              <Receipt className="w-5 h-5 shrink-0" /> Billing
            </NavLink>
          </div>

          {/* RESOURCES */}
          <div className="mt-4 pt-4 border-t border-slate-100 space-y-1">
            <p className="px-3.5 pb-2 text-[11px] font-semibold tracking-widest text-slate-400 uppercase">Resources</p>
            {isAdmin ? (
              <NavLink to="/docs" data-testid="nav-docs" className={sideNavClass}>
                <BookOpen className="w-5 h-5 shrink-0" /> Documentation
              </NavLink>
            ) : (
              <NavLink to="/app/help" data-testid="nav-help" className={sideNavClass}>
                <LifeBuoy className="w-5 h-5 shrink-0" /> Help &amp; Support
              </NavLink>
            )}
          </div>

          {/* ADMIN (admins only) */}
          {isAdmin && (
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-1">
              <p className="px-3.5 pb-2 text-[11px] font-semibold tracking-widest text-slate-400 uppercase">Admin</p>
              <NavLink to="/app/admin/users" data-testid="nav-users" className={sideNavClass}>
                <UsersRound className="w-5 h-5 shrink-0" /> User Management
              </NavLink>
              <NavLink to="/app/admin/templates" data-testid="nav-templates" className={sideNavClass}>
                <MailPlus className="w-5 h-5 shrink-0" /> Email Templates
              </NavLink>
              <NavLink to="/app/admin/contact-audit" data-testid="nav-contact-audit" className={sideNavClass}>
                <MailSearch className="w-5 h-5 shrink-0" /> Contact Audit
              </NavLink>
              <NavLink to="/app/admin/settings" data-testid="nav-settings" className={sideNavClass}>
                <SettingsIcon className="w-5 h-5 shrink-0" /> Settings
              </NavLink>
            </div>
          )}

          {/* BOTTOM ACTION */}
          <div className="mt-auto pt-4 border-t border-slate-200">
            <button data-testid="nav-back-main-app" onClick={goMainApp}
              className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors">
              <ArrowLeft className="w-5 h-5 shrink-0" /> Back to Main App
            </button>
            <p className="px-3.5 pt-3 text-[11px] leading-tight text-slate-400">
              <span className="font-semibold text-slate-500">JobBasket Network</span><br />
              Your next opportunity, closer.
            </p>
          </div>
        </nav>
      </aside>

      {/* ===== Main column ===== */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Desktop top utility bar */}
        <header className="hidden lg:flex sticky top-0 z-40 h-16 items-center justify-end gap-4 px-8 bg-[#F5F6F8]/90 backdrop-blur border-b border-slate-200">
          <div className="text-right leading-tight">
            <p className="text-sm font-semibold text-slate-900">{greeting()}, {user?.name?.split(" ")[0] || "there"} <span className="inline-block">👋</span></p>
            <p className="text-xs text-slate-500">Let's make today count!</p>
          </div>
          <TrialPill onClick={openUpgrade} />
          {isAdmin && (
            <a
              href={`/docs#${HELP_MAP.find(([p]) => location.pathname === p || (p !== "/app" && location.pathname.startsWith(p)))?.[1] || "dashboard"}`}
              target="_blank"
              rel="noreferrer"
              data-testid="help-docs-button"
              title="Open module documentation"
              className="w-9 h-9 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:text-amber-600 hover:border-amber-300 transition-colors"
            >
              <HelpCircle className="w-5 h-5" />
            </a>
          )}
          <button className="relative text-slate-500 hover:text-slate-900" data-testid="nav-bell">
            <Bell className="w-5 h-5" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="user-menu" className="flex items-center gap-1.5 outline-none">
              <div className="w-9 h-9 rounded-full bg-amber-500 text-white flex items-center justify-center text-sm font-bold">{initial}</div>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white border-slate-200 w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name || "Candidate"}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/app/profile")} className="gap-2 cursor-pointer"><UserRound className="w-4 h-4" /> Profile & Resume</DropdownMenuItem>
              {!quota?.is_pro && <DropdownMenuItem onClick={openUpgrade} className="gap-2 cursor-pointer text-amber-700"><Crown className="w-4 h-4" /> Upgrade to Pro</DropdownMenuItem>}
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="user-menu-back-main-app" onClick={goMainApp} className="gap-2 cursor-pointer"><ArrowLeft className="w-4 h-4" /> Back to Main App</DropdownMenuItem>
              <DropdownMenuItem data-testid="logout-button" onClick={handleLogout} className="gap-2 cursor-pointer text-rose-600"><LogOut className="w-4 h-4" /> Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-200 h-14 px-4 flex items-center justify-between">
          <Sheet open={drawer} onOpenChange={setDrawer}>
            <SheetTrigger data-testid="mobile-menu-button" className="text-slate-700"><Menu className="w-6 h-6" /></SheetTrigger>
            <SheetContent side="left" className="bg-white border-slate-200 w-[300px] p-0" data-testid="mobile-drawer">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <div className="p-5 border-b border-slate-200"><Logo /></div>
              <div className="p-4 flex items-center gap-3 border-b border-slate-200">
                <div className="w-11 h-11 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold">{initial}</div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{user?.name || "Candidate"}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                </div>
              </div>
              <nav className="p-3">
                {[
                  { label: "Main", items: [...PRIMARY, ...COMPANIES_CHILDREN] },
                  { label: "Account", items: [{ to: "/app/profile", label: "Profile & Resume", icon: UserRound, testid: "nav-profile" }, { to: isAdmin ? "/app/admin/plans" : "/app/pricing", label: "Plans & Pricing", icon: Tag, testid: "nav-plans" }, { to: isAdmin ? "/app/admin/billing" : "/app/billing", label: "Billing", icon: Receipt, testid: "nav-billing" }] },
                  { label: "Resources", items: [isAdmin
                      ? { to: "/docs", label: "Documentation", icon: BookOpen, testid: "nav-docs" }
                      : { to: "/app/help", label: "Help & Support", icon: LifeBuoy, testid: "nav-help" }] },
                  ...(isAdmin ? [{ label: "Admin", items: [{ to: "/app/admin/users", label: "User Management", icon: UsersRound, testid: "nav-users" }, { to: "/app/admin/templates", label: "Email Templates", icon: MailPlus, testid: "nav-templates" }, { to: "/app/admin/contact-audit", label: "Contact Audit", icon: MailSearch, testid: "nav-contact-audit" }, { to: "/app/admin/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" }] }] : []),
                ].map((group, gi) => (
                  <div key={group.label} className={gi > 0 ? "mt-4 pt-4 border-t border-slate-100" : ""}>
                    <p className="px-3 pb-2 text-[11px] font-semibold tracking-widest text-slate-400 uppercase">{group.label}</p>
                    {group.items.map((item) => (
                      <NavLink key={item.to} to={item.to} end={item.end} data-testid={`drawer-${item.testid}`} onClick={() => setDrawer(false)}
                        className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-amber-50 text-amber-700" : "text-slate-700 hover:bg-slate-100"}`}>
                        <item.icon className="w-5 h-5" /> {item.label}
                      </NavLink>
                    ))}
                  </div>
                ))}
              </nav>
              <div className="p-3 mt-auto border-t border-slate-200">
                <button data-testid="drawer-back-main-app" onClick={goMainApp} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 mb-1">
                  <ArrowLeft className="w-5 h-5" /> Back to Main App
                </button>
                {!quota?.is_pro && (
                  <button onClick={() => { setDrawer(false); openUpgrade(); }} className="w-full rounded-xl border border-amber-200 bg-amber-50 p-3 text-left mb-2">
                    <span className="flex items-center gap-2 text-amber-700 text-sm font-semibold"><Crown className="w-4 h-4" /> Upgrade to Pro</span>
                  </button>
                )}
                <button data-testid="drawer-logout" onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-rose-600 hover:bg-rose-50">
                  <LogOut className="w-5 h-5" /> Log Out
                </button>
              </div>
            </SheetContent>
          </Sheet>
          <Logo />
          <TrialPillMobile onClick={openUpgrade} />
        </header>

        {/* Main content */}
        <main className="flex-1 max-w-[1400px] w-full mx-auto p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8">
          <Outlet />
        </main>
      </div>

      {/* ===== Mobile bottom nav ===== */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 h-16 flex items-center justify-around px-1" data-testid="mobile-bottom-nav">
        {MOBILE_BOTTOM.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.testid}
            className={({ isActive }) => `relative flex flex-col items-center justify-center gap-0.5 flex-1 h-full text-[10px] font-medium transition-colors ${isActive ? "text-amber-600" : "text-slate-500"}`}>
            <span className="relative">
              <item.icon className="w-5 h-5" />
              {item.to === "/app/communications" && unreadReplies > 0 && (
                <span data-testid="mnav-replies-badge"
                  className="absolute -top-1.5 -right-2 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-black text-[9px] font-bold">
                  {unreadReplies > 9 ? "9+" : unreadReplies}
                </span>
              )}
            </span>
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

function TrialPillMobile({ onClick }) {
  const { quota } = useAuth();
  if (!quota) return <span className="w-6" />;
  if (quota.is_pro) return <span data-testid="trial-counter-badge-mobile" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-semibold"><Crown className="w-3 h-3" /> Pro</span>;
  const rem = quota.remaining ?? 0;
  const empty = rem <= 0;
  return (
    <button data-testid="trial-counter-badge-mobile" onClick={empty ? onClick : undefined}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${empty ? "bg-rose-50 border-rose-200 text-rose-600" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
      <Zap className="w-3 h-3" /> {empty ? "Upgrade" : `${rem} left`}
    </button>
  );
}
